// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A confirmation dialog for finalizing shipment receipts.
 * This modal shows a summary of what will be received and requires
 * confirmation before proceeding with the irreversible receive action.
 *
 * Features:
 * - Displays receipt summary with part details, received vs expected quantities
 * - Shows status indicators (green check for match, orange warning for discrepancy, red error for missing)
 * - Grey check for overage scenarios (received > 0 but expected is 0/null)
 * - Warning alerts for quantity discrepancies
 * - Irreversible action warning
 */
import {
  CancelOutlined,
  CheckCircleOutlined,
  WarningAmberOutlined,
} from '@/constants/hardfin-standard-icons';
import { Shipment } from '@/shipment/api/types';
import { formatNumber } from '@/utilities/number';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Fragment } from 'react';

interface ItemReceiptSummary {
  item: {
    id: string;
    sku: string;
    name: string;
    hasAssets: boolean;
  };
  receivedQuantity: number;
  expectedQuantity: number | null;
}

interface Props {
  /**
   * Whether the confirmation dialog is open.
   */
  isOpen: boolean;
  /**
   * Function to call when the dialog should be closed.
   */
  onClose: () => void;
  /**
   * Function to call when the user confirms the receipt.
   */
  onConfirm: () => void;
  /**
   * The shipment being received.
   */
  shipment: Shipment;
  /**
   * The items being received with their quantities.
   */
  itemsToReceive: ItemReceiptSummary[];
}

/**
 * Determines the status of an item based on received vs expected quantities.
 */
function getItemStatus(
  received: number,
  expected: number | null
): {
  type: 'success' | 'warning' | 'error' | 'overage';
  icon: React.ReactNode;
  color: string;
} {
  // If expected is 0 or null and received > 0, show grey check (overage scenario)
  if ((expected === 0 || expected === null) && received > 0) {
    return {
      type: 'overage',
      icon: <CheckCircleOutlined sx={{ color: 'text.secondary' }} />,
      color: 'text.secondary',
    };
  }

  // If received matches expected, show green check
  if (received === expected) {
    return {
      type: 'success',
      icon: <CheckCircleOutlined sx={{ color: 'success.main' }} />,
      color: 'success.main',
    };
  }

  // If nothing received but expected > 0, show red error
  if (received === 0 && expected && expected > 0) {
    return {
      type: 'error',
      icon: <CancelOutlined sx={{ color: 'error.main' }} />,
      color: 'error.main',
    };
  }

  // Any other discrepancy, show orange warning
  return {
    type: 'warning',
    icon: <WarningAmberOutlined sx={{ color: 'warning.main' }} />,
    color: 'warning.main',
  };
}

/**
 * Determines the overall status of the receipt.
 */
function getOverallStatus(itemsToReceive: ItemReceiptSummary[]): {
  type: 'success' | 'warning';
  title: string;
  message: string;
  alertSeverity: 'success' | 'warning';
} {
  const hasAnyDiscrepancies = itemsToReceive.some((item) => {
    const status = getItemStatus(item.receivedQuantity, item.expectedQuantity);
    return status.type === 'error' || status.type === 'warning';
  });

  if (hasAnyDiscrepancies) {
    return {
      type: 'warning',
      title: 'Quantity discrepancies detected',
      message:
        'Please verify the received quantities are correct before proceeding.',
      alertSeverity: 'warning',
    };
  }
  return {
    type: 'success',
    title: 'All received quantities match expected quantities',
    message: '',
    alertSeverity: 'success',
  };
}

/**
 * A confirmation dialog that shows the receipt summary before finalizing.
 *
 * @param props - Configuration options for the dialog
 */
export function ReceiveConfirmationDialog(props: Props) {
  const overallStatus = getOverallStatus(props.itemsToReceive);

  return (
    <Dialog open={props.isOpen} onClose={props.onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ px: 3, pt: 3 }}>
        Confirm receipt finalization
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        <Stack gap={2}>
          {/* Shipment context */}
          <Typography variant="body1">
            You are about to finalize the receipt for shipment{' '}
            <strong>{props.shipment.shipmentNumber}</strong>.
          </Typography>

          {/* Warning about irreversible action */}
          <Alert severity="error">
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                This action cannot be undone
              </Typography>
              <Typography variant="body2">
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  <li>All shipment line items will be locked</li>
                  <li>
                    Parts will automatically be assigned to a lot and enter
                    inventory
                  </li>
                  <li>
                    Products will be imported as assemblies and registered as
                    assets
                  </li>
                  <li>
                    Shipped and received quantities will not be able to be
                    adjusted
                  </li>
                </ul>
              </Typography>
            </Box>
          </Alert>

          {/* Receipt summary */}
          <Paper
            variant="outlined"
            sx={{ p: 3, borderRadius: 2 }}
            elevation={0}
          >
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Receipt summary
            </Typography>

            <Stack gap={1.5}>
              {props.itemsToReceive.map((itemSummary, index) => {
                const status = getItemStatus(
                  itemSummary.receivedQuantity,
                  itemSummary.expectedQuantity
                );

                const displaySku = itemSummary.item.sku;

                return (
                  <Fragment key={itemSummary.item.id}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack>
                        <Typography variant="subtitle2">
                          {displaySku}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {itemSummary.item.name}
                        </Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" gap={1}>
                        <Stack alignItems="flex-end">
                          <Typography
                            variant="body2"
                            sx={{ color: status.color, fontWeight: 500 }}
                          >
                            Received:{' '}
                            {formatNumber(itemSummary.receivedQuantity)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Expected:{' '}
                            {itemSummary.expectedQuantity !== null
                              ? formatNumber(itemSummary.expectedQuantity)
                              : '0'}
                          </Typography>
                        </Stack>
                        {status.icon}
                      </Stack>
                    </Stack>
                    {index < props.itemsToReceive.length - 1 && <Divider />}
                  </Fragment>
                );
              })}
            </Stack>
          </Paper>

          {/* Status message */}

          {overallStatus.type !== 'success' && (
            <Alert
              severity={overallStatus.alertSeverity}
              icon={<WarningAmberOutlined />}
            >
              <Typography variant="subtitle2">{overallStatus.title}</Typography>
              {overallStatus.message && (
                <Typography variant="body2">{overallStatus.message}</Typography>
              )}
            </Alert>
          )}

          {/* Confirmation question */}
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Are you sure you want to confirm this receipt?
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button onClick={props.onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={props.onConfirm} variant="contained" color="primary">
          Yes, confirm receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
}
